import { useState, useEffect } from "react";
import { api } from "@/App";
import { Plus, Trash2, GripVertical, Wrench, ArrowRight, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function WorkflowTab() {
  const [steps, setSteps] = useState([]);
  const [specialites, setSpecialites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [newStep, setNewStep] = useState({
    label: "",
    specialite: "carrossier"
  });

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [stepsRes, specsRes] = await Promise.all([
        api.get("/workflow-steps"),
        api.get("/specialites")
      ]);
      const dedupe = (arr) => Array.from(new Map((arr || []).map(o => [o.id, o])).values());
      setSteps(dedupe(stepsRes.data));
      setSpecialites(dedupe(specsRes.data));
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
    setLoading(false);
  };

  const handleAddStep = async () => {
    if (!newStep.label) {
      toast.error("Veuillez entrer un nom");
      return;
    }
    setSaving(true);
    try {
      const name = newStep.label.toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, "");
      
      await api.post("/workflow-steps", {
        name,
        label: newStep.label,
        specialite: newStep.specialite,
        ordre: steps.length + 1
      });
      toast.success("Étape ajoutée");
      setDialogOpen(false);
      setNewStep({ label: "", specialite: "carrossier" });
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
    setSaving(false);
  };

  const handleDeleteStep = async (stepId) => {
    try {
      await api.delete(`/workflow-steps/${stepId}`);
      toast.success("Étape supprimée");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleMoveStep = async (stepId, direction) => {
    const currentIndex = steps.findIndex(s => s.id === stepId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= steps.length) return;
    
    // Créer une nouvelle liste avec les positions échangées
    const newSteps = [...steps];
    [newSteps[currentIndex], newSteps[newIndex]] = [newSteps[newIndex], newSteps[currentIndex]];
    
    try {
      // Mettre à jour tous les ordres pour qu'ils soient consécutifs
      const updates = newSteps.map((step, idx) => 
        api.put(`/workflow-steps/${step.id}`, { ordre: idx + 1 })
      );
      await Promise.all(updates);
      toast.success("Ordre modifié");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors du déplacement");
    }
  };

  const getSpecialiteLabel = (name) => {
    const spec = specialites.find(s => s.name === name);
    return spec?.label || name;
  };

  const getSpecialiteColor = (name) => {
    const colors = {
      carrossier: "bg-orange-100 text-orange-700",
      peintre: "bg-blue-100 text-blue-700",
      mecanicien: "bg-emerald-100 text-emerald-700",
      preparateur_peintre: "bg-purple-100 text-purple-700",
      laveur: "bg-cyan-100 text-cyan-700",
    };
    return colors[name] || "bg-slate-100 text-slate-700";
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Étapes du workflow
            </CardTitle>
            <CardDescription>
              Définissez les étapes de progression des véhicules en carrosserie
            </CardDescription>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="bg-red-600 hover:bg-red-700">
            <Plus className="h-4 w-4 mr-2" />
            Ajouter
          </Button>
        </CardHeader>
        <CardContent>
          {/* Workflow Preview */}
          <div className="mb-6 p-4 bg-slate-50 rounded-lg">
            <p className="text-xs font-semibold text-slate-500 mb-3">APERÇU DU WORKFLOW</p>
            <div className="flex flex-wrap items-center gap-2">
              {steps.map((step, idx) => (
                <div key={step.id} className="flex items-center gap-2">
                  <Badge className={getSpecialiteColor(step.specialite)}>
                    {step.label}
                  </Badge>
                  {idx < steps.length - 1 && (
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                  )}
                </div>
              ))}
              <ArrowRight className="h-4 w-4 text-slate-400" />
              <Badge className="bg-emerald-100 text-emerald-700">Terminé</Badge>
            </div>
          </div>

          {/* Steps List */}
          <div className="space-y-2">
            {steps.map((step, idx) => (
              <div
                key={step.id}
                className="flex items-center gap-4 p-3 rounded-lg border border-slate-200 bg-white"
              >
                <div className="flex items-center gap-2 text-slate-400">
                  <GripVertical className="h-4 w-4" />
                  <span className="text-sm font-mono w-6">{idx + 1}</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{step.label}</p>
                </div>
                <Badge className={getSpecialiteColor(step.specialite)}>
                  {getSpecialiteLabel(step.specialite)}
                </Badge>
                {/* Boutons monter/descendre */}
                <div className="flex flex-col gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-slate-500 hover:bg-slate-100"
                    onClick={() => handleMoveStep(step.id, "up")}
                    disabled={idx === 0}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-slate-500 hover:bg-slate-100"
                    onClick={() => handleMoveStep(step.id, "down")}
                    disabled={idx === steps.length - 1}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => handleDeleteStep(step.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Add Step Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une étape</DialogTitle>
            <DialogDescription>Créer une nouvelle étape dans le workflow</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom de l'étape *</Label>
              <Input
                value={newStep.label}
                onChange={(e) => setNewStep({ ...newStep, label: e.target.value })}
                placeholder="Ex: Préparation, Lustrage..."
              />
            </div>
            <div className="space-y-2">
              <Label>Spécialité responsable</Label>
              <Select
                value={newStep.specialite}
                onValueChange={(v) => setNewStep({ ...newStep, specialite: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {specialites.filter(s => s.name !== "admin" && s.name !== "secretaire").map((spec) => (
                    <SelectItem key={spec.id} value={spec.name}>{spec.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleAddStep} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <div className="spinner" /> : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
